import { Delete, DragIndicator, OpenInNew, Search } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useDebounce } from 'src/hooks/use-debounce';
import { PermissionButton } from 'src/routes/components';
import dayjs from 'dayjs';
import { formatDayLabel } from '../utils';
import EventsList from './EventsList';

const APP_URL = process.env.REACT_APP_APP_URL ?? '';

const buildEventUrl = (slug?: string | null) => {
  if (!slug) return '';
  const base = APP_URL.replace(/\/+$/, '');
  return `${base}/prediction/sports/event/${slug}`;
};

const getDisplayLabel = (item: any) => {
  if ((item.type as string) === 'EVENT') return item.title || item.question || '';
  return item.question || item.title || '';
};

const formatStartTime = (value?: string | null) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '';
};

function SortableRow({
  id,
  item,
  index,
  onRemove,
}: {
  id: string;
  item: any;
  index: number;
  onRemove: (index: number) => void;
}) {
  const translate = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card variant="outlined">
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton
              size="small"
              sx={{ cursor: 'grab', color: 'text.disabled' }}
              {...attributes}
              {...listeners}
            >
              <DragIndicator fontSize="small" />
            </IconButton>
            <Avatar
              src={item.image || item.icon}
              alt={getDisplayLabel(item)}
              variant="rounded"
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {getDisplayLabel(item)}
              </Typography>
              {formatStartTime(item.startTime) ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {translate('module.worldCup.breakingEvents.startTime', {
                    time: formatStartTime(item.startTime),
                  })}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                ID: {item.id}
              </Typography>
              {item.slug && APP_URL ? (
                <Link
                  href={buildEventUrl(item.slug)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.3,
                    mt: 0.5,
                  }}
                >
                  {item.slug}
                  <OpenInNew sx={{ fontSize: 12 }} />
                </Link>
              ) : null}
            </Box>
            <PermissionButton resource="world-cup-custom-breaking" action="write">
              <IconButton color="error" onClick={() => onRemove(index)}>
                <Delete />
              </IconButton>
            </PermissionButton>
          </Stack>
        </CardContent>
      </Card>
    </div>
  );
}

type DayEventsDialogProps = {
  open: boolean;
  day: string;
  items: any[];
  dayByMarketId: Record<string, string>;
  events: any[];
  eventsLoading?: boolean;
  onClose: () => void;
  onReorder: (oldIndex: number, newIndex: number) => void;
  onRemove: (index: number) => void;
  onSelectItem: (item: any) => void;
};

export default function DayEventsDialog({
  open,
  day,
  items,
  dayByMarketId,
  events,
  eventsLoading,
  onClose,
  onReorder,
  onRemove,
  onSelectItem,
}: DayEventsDialogProps) {
  const translate = useTranslate();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 300);

  useEffect(() => {
    if (open) setSearchValue('');
  }, [open]);

  const selectedIds = useMemo(() => new Set(Object.keys(dayByMarketId)), [dayByMarketId]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => `${item.id}` === `${active.id}`);
      const newIndex = items.findIndex((item) => `${item.id}` === `${over.id}`);
      if (oldIndex !== -1 && newIndex !== -1) onReorder(oldIndex, newIndex);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box>
          <Typography variant="h6">{formatDayLabel(day)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            minHeight: 480,
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">
              {translate('module.worldCup.breakingEvents.pinnedTitle')}
            </Typography>
            <Divider />
            {items.length === 0 ? (
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {translate('module.worldCup.breakingEvents.empty')}
                </Typography>
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((item) => `${item.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1.5} sx={{ maxHeight: 480, overflowY: 'auto' }}>
                    {items.map((item, index) => (
                      <SortableRow
                        key={`${item.id}`}
                        id={`${item.id}`}
                        item={item}
                        index={index}
                        onRemove={onRemove}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">
              {translate('module.worldCup.breakingEvents.browseTitle')}
            </Typography>
            <Divider />
            <TextField
              fullWidth
              size="small"
              placeholder={translate('module.worldCup.breakingEvents.searchPlaceholder')}
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <EventsList
              events={events}
              loading={eventsLoading}
              searchValue={debouncedSearchValue}
              selectedDay={day}
              selectedIds={selectedIds}
              onSelectItem={onSelectItem}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('ra.action.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export { arrayMove };
